import { callGeotabAPI } from '../api/geotab-client';
import type { HeatPoint, DangerZone, TimeSlot, DayOfWeek, RuleKey } from './heatmap-types';
import { RULE_KEY_ORDER } from './heatmap-types';

const EARTH_R = 6371000; // metres

export function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.sqrt(a));
}

// Above this many points, cluster a representative even sample. DBSCAN is run again on every
// filter change, so this keeps the worst case bounded even for pathological point density. The
// heat layer itself still renders every point — only this "top danger zones" summary is sampled.
const MAX_CLUSTER_POINTS = 40000;

function sampleEvenly(points: HeatPoint[], max: number): HeatPoint[] {
  if (points.length <= max) return points;
  const out: HeatPoint[] = [];
  const step = points.length / max;
  for (let i = 0; i < max; i++) out.push(points[Math.floor(i * step)]);
  return out;
}

export function clusterPoints(
  points: HeatPoint[],
  epsilonM = 300,
  minPoints = 3,
): DangerZone[] {
  if (points.length === 0) return [];

  const sample = sampleEvenly(points, MAX_CLUSTER_POINTS);
  const n = sample.length;
  const labels = new Int32Array(n).fill(-1); // -1 = unvisited
  let clusterId = 0;

  // Spatial grid (cell ≈ epsilon) so a point's neighbours are only ever in the 3×3 cells around
  // it — turns the old O(n²) "scan every point for every point" into ~O(n). The old version froze
  // the page for minutes on large fleets (it also re-runs on every filter change).
  const latStep = epsilonM / 111_320;
  let latSum = 0;
  for (let i = 0; i < n; i++) latSum += sample[i].latitude;
  const meanLat = latSum / n;
  const lngStep = epsilonM / (111_320 * Math.max(0.05, Math.cos(meanLat * Math.PI / 180)));

  const cellOf = (lat: number, lng: number) =>
    Math.floor(lat / latStep) + ':' + Math.floor(lng / lngStep);

  const grid = new Map<string, number[]>();
  for (let i = 0; i < n; i++) {
    const k = cellOf(sample[i].latitude, sample[i].longitude);
    const cell = grid.get(k);
    if (cell) cell.push(i);
    else grid.set(k, [i]);
  }

  const neighbours = (idx: number): number[] => {
    const p = sample[idx];
    const cl = Math.floor(p.latitude / latStep);
    const cg = Math.floor(p.longitude / lngStep);
    const result: number[] = [];
    for (let a = -1; a <= 1; a++) {
      for (let b = -1; b <= 1; b++) {
        const cell = grid.get((cl + a) + ':' + (cg + b));
        if (!cell) continue;
        for (const i of cell) {
          if (i === idx) continue;
          if (haversineM(p.latitude, p.longitude, sample[i].latitude, sample[i].longitude) <= epsilonM) {
            result.push(i);
          }
        }
      }
    }
    return result;
  };

  for (let i = 0; i < n; i++) {
    if (labels[i] !== -1) continue;
    const nb = neighbours(i);
    if (nb.length < minPoints - 1) {
      labels[i] = 0; // noise
      continue;
    }
    clusterId++;
    labels[i] = clusterId;
    const seeds = [...nb];
    while (seeds.length > 0) {
      const j = seeds.pop()!;
      if (labels[j] === 0) labels[j] = clusterId;
      if (labels[j] !== -1) continue;
      labels[j] = clusterId;
      const nb2 = neighbours(j);
      if (nb2.length >= minPoints - 1) seeds.push(...nb2);
    }
  }

  const groups = new Map<number, HeatPoint[]>();
  for (let i = 0; i < n; i++) {
    const c = labels[i];
    if (c <= 0) continue;
    if (!groups.has(c)) groups.set(c, []);
    groups.get(c)!.push(sample[i]);
  }

  const zones: DangerZone[] = [];
  let rank = 0;
  for (const [, members] of groups) {
    const centroidLat = members.reduce((s, p) => s + p.latitude,  0) / members.length;
    const centroidLng = members.reduce((s, p) => s + p.longitude, 0) / members.length;
    const radiusM     = Math.max(...members.map(p => haversineM(centroidLat, centroidLng, p.latitude, p.longitude)));

    const byRule: Partial<Record<RuleKey, number>> = {};
    for (const p of members) {
      byRule[p.ruleKey] = (byRule[p.ruleKey] ?? 0) + 1;
    }
    const dominantRule = (RULE_KEY_ORDER
      .filter(k => byRule[k] !== undefined)
      .sort((a, b) => (byRule[b] ?? 0) - (byRule[a] ?? 0))[0]) as RuleKey;

    const hours = members.map(p => new Date(p.timestamp).getHours());
    const hourFreq: Record<number, number> = {};
    for (const h of hours) hourFreq[h] = (hourFreq[h] ?? 0) + 1;
    const peakHour = Number(Object.entries(hourFreq).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null);

    rank++;
    zones.push({
      id: `dz-${rank}`,
      centroidLat,
      centroidLng,
      radiusM,
      totalViolations: members.length,
      dominantRule,
      byRule,
      uniqueDeviceIds: new Set(members.map(p => p.deviceId)),
      uniqueDriverIds: new Set(members.filter(p => p.driverId).map(p => p.driverId as string)),
      locationName: null,
      peakHour: isNaN(peakHour) ? null : peakHour,
      rank,
      points: members,
    });
  }

  zones.sort((a, b) => b.totalViolations - a.totalViolations);
  zones.slice(0, 10).forEach((z, i) => { z.rank = i + 1; });
  return zones.slice(0, 10);
}

const TIME_SLOT_RANGES: Record<TimeSlot, (h: number) => boolean> = {
  all:         () => true,
  morningRush: h => h >= 6  && h <= 8,
  midday:      h => h >= 9  && h <= 16,
  eveningRush: h => h >= 17 && h <= 19,
  night:       h => h >= 20 || h <= 5,
};

export function filterPoints(
  points: HeatPoint[],
  activeRuleKeys: Set<RuleKey>,
  activeTimeSlot: TimeSlot,
  activeDays: Set<DayOfWeek>,
): HeatPoint[] {
  const slotFn = TIME_SLOT_RANGES[activeTimeSlot];
  return points.filter(p => {
    if (!activeRuleKeys.has(p.ruleKey)) return false;
    const d = new Date(p.timestamp);
    if (activeDays.size < 7 && !activeDays.has(d.getDay() as DayOfWeek)) return false;
    if (activeTimeSlot !== 'all' && !slotFn(d.getHours())) return false;
    return true;
  });
}

export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    // Use Geotab's built-in address lookup (already authenticated, no rate limits)
    const results = await callGeotabAPI<Array<{ formattedAddress?: string }>>('GetAddresses', {
      coordinates: [{ x: lng, y: lat }],
    });
    return results?.[0]?.formattedAddress?.trim() ?? null;
  } catch {
    return null;
  }
}
